import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantViewings } from '../../hooks/useViewings';
import { formatTime, formatDate } from '../../services/viewings.service';
import { 
  downloadICSFile, 
  openGoogleCalendar,
  generateWebcalSubscriptionURL,
  generateGoogleCalendarSubscriptionURL
} from '../../services/calendar.service';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  User,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  List,
  ExternalLink,
  Search,
  CheckCircle2,
  X,
  Apple
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function TenantAgendaNew() {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'list'
  const [selectedViewing, setSelectedViewing] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSyncModal, setShowSyncModal] = useState(false);
  
  // Load confirmed viewings for tenant
  const { viewings, loading } = useTenantViewings('confirmed');

  // Filter viewings based on search
  const filteredViewings = useMemo(() => {
    if (!searchQuery) return viewings;
    
    const query = searchQuery.toLowerCase();
    return viewings.filter(viewing => {
      const propertyTitle = (viewing.propertyTitle || viewing.listingId || '').toLowerCase();
      const landlordName = (viewing.landlordName || '').toLowerCase();
      const location = (viewing.location || viewing.propertyAddress || '').toLowerCase();
      const notes = (viewing.notes || '').toLowerCase();
      
      return propertyTitle.includes(query) || 
             landlordName.includes(query) || 
             location.includes(query) ||
             notes.includes(query);
    });
  }, [viewings, searchQuery]);

  // Get viewings for a specific date
  const getViewingsForDate = (date) => {
    if (!date) return [];
    return filteredViewings.filter(viewing => {
      const viewingStart = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
      return viewingStart.toDateString() === date.toDateString();
    });
  };

  // Get viewings for current week
  const getViewingsForWeek = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return filteredViewings.filter(viewing => {
      const viewingStart = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
      return viewingStart >= startOfWeek && viewingStart <= endOfWeek;
    });
  };

  // Calendar navigation
  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const navigateWeek = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setDate(newDate.getDate() - 7);
      } else {
        newDate.setDate(newDate.getDate() + 7);
      }
      return newDate;
    });
  };

  // Export handlers
  const handleExportToApple = (viewing) => {
    try {
      downloadICSFile(viewing);
      toast.success('Geëxporteerd naar Apple Calendar');
    } catch (error) {
      console.error('Error exporting to Apple Calendar:', error);
      toast.error('Fout bij exporteren naar Apple Calendar');
    }
  };

  const handleExportToGoogle = (viewing) => {
    try {
      openGoogleCalendar(viewing);
      toast.success('Opening Google Calendar...');
    } catch (error) {
      console.error('Error exporting to Google Calendar:', error);
      toast.error('Fout bij exporteren naar Google Calendar');
    }
  };

  const monthNames = [
    'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
    'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
  ];
  const dayNames = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
  const dayNamesFull = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];

  // Helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getWeekDays = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ios-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Bezichtigingen laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="container-professional py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Agenda</h1>
              <p className="text-gray-600">
                Bekijk je geaccepteerde bezichtigingen en synchroniseer met je favoriete agenda app
              </p>
            </div>
            
            {/* Export Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowSyncModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all hover:scale-105 shadow-md"
              >
                {/* Apple Logo */}
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <span>Apple Calendar</span>
              </button>
              <button
                onClick={() => setShowSyncModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all hover:scale-105 shadow-md"
              >
                {/* Google Logo */}
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Google Calendar</span>
              </button>
            </div>
          </div>

          {/* Search and View Toggle */}
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Zoek bezichtigingen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ios-blue focus:border-transparent"
              />
            </div>
            
            <div className="flex items-center space-x-2 bg-white rounded-lg p-1 border border-gray-200">
              <button
                onClick={() => setViewMode('month')}
                className={`px-4 py-2 rounded-md transition-all ${
                  viewMode === 'month' 
                    ? 'bg-ios-blue text-white shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <CalendarDays className="w-4 h-4 inline mr-2" />
                Maand
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-4 py-2 rounded-md transition-all ${
                  viewMode === 'week' 
                    ? 'bg-ios-blue text-white shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Calendar className="w-4 h-4 inline mr-2" />
                Week
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-md transition-all ${
                  viewMode === 'list' 
                    ? 'bg-ios-blue text-white shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <List className="w-4 h-4 inline mr-2" />
                Lijst
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Views */}
        {viewMode === 'month' && <MonthView 
          currentDate={currentDate}
          navigateMonth={navigateMonth}
          setCurrentDate={setCurrentDate}
          getViewingsForDate={getViewingsForDate}
          monthNames={monthNames}
          dayNames={dayNames}
          getDaysInMonth={getDaysInMonth}
          setSelectedViewing={setSelectedViewing}
        />}

        {viewMode === 'week' && <WeekView 
          currentDate={currentDate}
          navigateWeek={navigateWeek}
          setCurrentDate={setCurrentDate}
          getViewingsForDate={getViewingsForDate}
          getViewingsForWeek={getViewingsForWeek}
          dayNamesFull={dayNamesFull}
          monthNames={monthNames}
          getWeekDays={getWeekDays}
          getWeekNumber={getWeekNumber}
          setSelectedViewing={setSelectedViewing}
        />}

        {viewMode === 'list' && <ListView 
          viewings={filteredViewings}
          setSelectedViewing={setSelectedViewing}
        />}

        {/* Viewing Detail Modal */}
        {selectedViewing && (
          <ViewingDetailModal
            viewing={selectedViewing}
            onClose={() => setSelectedViewing(null)}
            onExportToApple={handleExportToApple}
            onExportToGoogle={handleExportToGoogle}
          />
        )}

        {/* Calendar Sync Modal */}
        {showSyncModal && (
          <CalendarSyncModal
            userId={user?.uid}
            onClose={() => setShowSyncModal(false)}
            onExportToApple={handleExportToApple}
            onExportToGoogle={handleExportToGoogle}
            viewings={filteredViewings}
          />
        )}
      </div>
    </div>
  );
}

// Month View Component
function MonthView({ currentDate, navigateMonth, setCurrentDate, getViewingsForDate, monthNames, dayNames, getDaysInMonth, setSelectedViewing }) {
  const days = getDaysInMonth(currentDate);
  const today = new Date();
  
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Calendar Header */}
      <div className="bg-gradient-to-r from-ios-blue to-ios-blue-dark text-white p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-4 py-2 text-sm font-medium bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              Vandaag
            </button>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        <div className="grid grid-cols-7 gap-2 mb-2">
          {dayNames.map(day => (
            <div key={day} className="p-2 text-center text-sm font-semibold text-gray-600">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-2">
          {days.map((day, index) => {
            const viewings = getViewingsForDate(day);
            const isToday = day && day.toDateString() === today.toDateString();
            const isCurrentMonth = day && day.getMonth() === currentDate.getMonth();
            
            return (
              <div
                key={index}
                className={`min-h-[120px] p-2 rounded-lg border-2 transition-all ${
                  day && isCurrentMonth
                    ? isToday
                      ? 'bg-blue-50 border-ios-blue shadow-md'
                      : 'bg-white border-gray-200 hover:border-ios-blue/50 hover:shadow-sm'
                    : 'bg-gray-50 border-gray-100'
                }`}
              >
                {day && (
                  <>
                    <div className={`text-sm font-semibold mb-2 ${
                      isToday ? 'text-ios-blue' : 'text-gray-900'
                    }`}>
                      {day.getDate()}
                    </div>
                    
                    <div className="space-y-1">
                      {viewings.slice(0, 3).map(viewing => {
                        const startDate = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
                        return (
                          <button
                            key={viewing.id}
                            onClick={() => setSelectedViewing(viewing)}
                            className="w-full text-left text-xs bg-gradient-to-r from-ios-blue to-ios-blue-dark text-white px-2 py-1 rounded hover:shadow-md transition-all hover:scale-105 truncate"
                            title={`${formatTime(startDate)} - ${viewing.propertyTitle || viewing.listingId || 'Bezichtiging'}`}
                          >
                            {formatTime(startDate)}
                          </button>
                        );
                      })}
                      {viewings.length > 3 && (
                        <div className="text-xs text-gray-500 px-2">
                          +{viewings.length - 3} meer
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Week View Component
function WeekView({ currentDate, navigateWeek, setCurrentDate, getViewingsForDate, getViewingsForWeek, dayNamesFull, monthNames, getWeekDays, getWeekNumber, setSelectedViewing }) {
  const weekDays = getWeekDays(currentDate);
  const today = new Date();
  
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Week Header */}
      <div className="bg-gradient-to-r from-ios-blue to-ios-blue-dark text-white p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">
            Week {getWeekNumber(currentDate)} - {weekDays[0].getDate()} {monthNames[weekDays[0].getMonth()]} t/m {weekDays[6].getDate()} {monthNames[weekDays[6].getMonth()]} {weekDays[6].getFullYear()}
          </h2>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateWeek('prev')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-4 py-2 text-sm font-medium bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              Deze Week
            </button>
            <button
              onClick={() => navigateWeek('next')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Week Grid */}
      <div className="p-4">
        <div className="grid grid-cols-7 gap-4">
          {weekDays.map((day, index) => {
            const viewings = getViewingsForDate(day);
            const isToday = day.toDateString() === today.toDateString();
            
            return (
              <div
                key={index}
                className={`min-h-[400px] p-3 rounded-lg border-2 ${
                  isToday
                    ? 'bg-blue-50 border-ios-blue shadow-md'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className={`text-center mb-3 ${
                  isToday ? 'text-ios-blue font-bold' : 'text-gray-900'
                }`}>
                  <div className="text-xs text-gray-500 mb-1">{dayNamesFull[day.getDay()]}</div>
                  <div className="text-lg font-semibold">{day.getDate()}</div>
                </div>
                
                <div className="space-y-2">
                  {viewings.map(viewing => {
                    const startDate = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
                    const endDate = viewing.endAt?.toDate ? viewing.endAt.toDate() : new Date(viewing.endAt);
                    return (
                      <button
                        key={viewing.id}
                        onClick={() => setSelectedViewing(viewing)}
                        className="w-full text-left p-2 bg-gradient-to-r from-ios-blue to-ios-blue-dark text-white rounded-lg hover:shadow-lg transition-all hover:scale-105"
                      >
                        <div className="text-xs font-semibold mb-1">
                          {formatTime(startDate)} - {formatTime(endDate)}
                        </div>
                        <div className="text-xs opacity-90 truncate">
                          {viewing.propertyTitle || viewing.listingId || 'Bezichtiging'}
                        </div>
                        {viewing.landlordName && (
                          <div className="text-xs opacity-75 mt-1">
                            <User className="w-3 h-3 inline mr-1" />
                            {viewing.landlordName}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// List View Component
function ListView({ viewings, setSelectedViewing }) {
  const sortedViewings = [...viewings].sort((a, b) => {
    const dateA = a.startAt?.toDate ? a.startAt.toDate() : new Date(a.startAt);
    const dateB = b.startAt?.toDate ? b.startAt.toDate() : new Date(b.startAt);
    return dateA - dateB;
  });

  // Group by date
  const groupedByDate = sortedViewings.reduce((acc, viewing) => {
    const startDate = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
    const dateKey = startDate.toDateString();
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(viewing);
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-ios-blue to-ios-blue-dark text-white p-6">
        <h2 className="text-2xl font-bold">Aankomende Bezichtigingen</h2>
        <p className="text-white/80 mt-1">{sortedViewings.length} bezichtiging{sortedViewings.length !== 1 ? 'en' : ''}</p>
      </div>

      <div className="p-6">
        {sortedViewings.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Geen bezichtigingen</h3>
            <p className="text-gray-600">Geaccepteerde bezichtigingen verschijnen hier automatisch</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedByDate).map(([dateKey, dayViewings]) => {
              const date = new Date(dateKey);
              const isToday = date.toDateString() === new Date().toDateString();
              
              return (
                <div key={dateKey} className="space-y-3">
                  <div className={`flex items-center space-x-3 mb-2 ${
                    isToday ? 'text-ios-blue' : 'text-gray-600'
                  }`}>
                    <CalendarDays className="w-5 h-5" />
                    <h3 className="font-semibold text-lg">
                      {isToday ? 'Vandaag' : formatDate(date)} - {dayViewings.length} bezichtiging{dayViewings.length !== 1 ? 'en' : ''}
                    </h3>
                  </div>
                  
                  <div className="space-y-3 ml-8">
                    {dayViewings.map(viewing => (
                      <ViewingCard 
                        key={viewing.id} 
                        viewing={viewing} 
                        onClick={() => setSelectedViewing(viewing)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Viewing Card Component
function ViewingCard({ viewing, onClick }) {
  const startDate = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
  const endDate = viewing.endAt?.toDate ? viewing.endAt.toDate() : new Date(viewing.endAt);

  return (
    <div
      onClick={onClick}
      className="bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-ios-blue rounded-lg p-4 hover:shadow-lg transition-all cursor-pointer hover:scale-[1.02]"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-ios-blue to-ios-blue-dark rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {formatTime(startDate)} - {formatTime(endDate)}
              </h3>
              <p className="text-sm text-gray-600">
                {viewing.propertyTitle || viewing.listingId || 'Bezichtiging'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600 ml-16">
            {viewing.landlordName && (
              <div className="flex items-center">
                <User className="w-4 h-4 mr-1" />
                {viewing.landlordName}
              </div>
            )}
            {viewing.location && (
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                {viewing.location}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Bevestigd
          </span>
        </div>
      </div>
    </div>
  );
}

// Viewing Detail Modal
function ViewingDetailModal({ viewing, onClose, onExportToApple, onExportToGoogle }) {
  const startDate = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
  const endDate = viewing.endAt?.toDate ? viewing.endAt.toDate() : new Date(viewing.endAt);
  const [property, setProperty] = useState(null);
  const [loadingProperty, setLoadingProperty] = useState(true);
  const [landlord, setLandlord] = useState(null);
  const [loadingLandlord, setLoadingLandlord] = useState(true);

  // Get property ID - check both listingId and kotID
  const propertyId = viewing.listingId || viewing.kotID;
  // Get landlord ID - check different field names
  const landlordId = viewing.landlordUid || viewing.landlordId || viewing.landlordID;

  useEffect(() => {
    if (propertyId) {
      loadProperty();
    } else {
      setLoadingProperty(false);
    }
    
    if (landlordId) {
      loadLandlord();
    } else {
      setLoadingLandlord(false);
    }
  }, [propertyId, landlordId]);

  const loadProperty = async () => {
    try {
      setLoadingProperty(true);
      const propertyRef = doc(db, 'koten', propertyId);
      const propertySnap = await getDoc(propertyRef);
      
      if (propertySnap.exists()) {
        setProperty({
          id: propertySnap.id,
          ...propertySnap.data()
        });
      }
    } catch (error) {
      console.error('Error loading property:', error);
    } finally {
      setLoadingProperty(false);
    }
  };

  const loadLandlord = async () => {
    try {
      setLoadingLandlord(true);
      const landlordRef = doc(db, 'users', landlordId);
      const landlordSnap = await getDoc(landlordRef);
      
      if (landlordSnap.exists()) {
        setLandlord({
          id: landlordSnap.id,
          ...landlordSnap.data()
        });
      }
    } catch (error) {
      console.error('Error loading landlord:', error);
    } finally {
      setLoadingLandlord(false);
    }
  };

  // Format full address
  const formatFullAddress = () => {
    if (property) {
      const address = property.adres || property.address || '';
      const postalCode = property.postcode || property.postalCode || '';
      const city = property.stad || property.city || '';
      
      const parts = [address, postalCode, city].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : null;
    }
    
    // Fallback to viewing.location if available
    return viewing.location || null;
  };

  // Get property title
  const getPropertyTitle = () => {
    if (property) {
      return property.titel || property.title || property.naam || property.name || null;
    }
    return viewing.propertyTitle || null;
  };

  // Get landlord name
  const getLandlordName = () => {
    if (landlord) {
      return landlord.displayName || landlord.name || landlord.naam || landlord.email || null;
    }
    return viewing.landlordName || null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-ios-blue to-ios-blue-dark text-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Bezichtiging Details</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Datum</label>
              <p className="text-lg font-semibold text-gray-900">{formatDate(startDate)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Tijd</label>
              <p className="text-lg font-semibold text-gray-900">
                {formatTime(startDate)} - {formatTime(endDate)}
              </p>
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-600">Pand</label>
              {loadingProperty ? (
                <p className="text-lg font-semibold text-gray-400">Laden...</p>
              ) : (
                <p className="text-lg font-semibold text-gray-900">
                  {getPropertyTitle() || viewing.listingId || viewing.kotID || 'Niet opgegeven'}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-600">Verhuurder</label>
              {loadingLandlord ? (
                <p className="text-lg font-semibold text-gray-400">Laden...</p>
              ) : (
                <p className="text-lg font-semibold text-gray-900">
                  {getLandlordName() || 'Niet opgegeven'}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-600">Locatie</label>
              {loadingProperty ? (
                <p className="text-lg font-semibold text-gray-400">Laden...</p>
              ) : (
                <p className="text-lg font-semibold text-gray-900">
                  {formatFullAddress() || 'Niet opgegeven'}
                </p>
              )}
            </div>
            {viewing.notes && (
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-600">Notities</label>
                <p className="text-gray-900">{viewing.notes}</p>
              </div>
            )}
          </div>

          {/* Export Buttons */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Exporteren naar Agenda</h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => {
                  onExportToApple(viewing);
                  onClose();
                }}
                className="flex items-center justify-center space-x-2 p-4 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all hover:scale-105"
              >
                {/* Apple Logo */}
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <span>Apple Calendar</span>
              </button>
              <button
                onClick={() => {
                  onExportToGoogle(viewing);
                }}
                className="flex items-center justify-center space-x-2 p-4 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all hover:scale-105"
              >
                {/* Google Logo */}
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Google Calendar</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Calendar Sync Modal Component
function CalendarSyncModal({ userId, onClose, onExportToApple, onExportToGoogle, viewings }) {
  const [syncType, setSyncType] = useState(null); // 'apple' or 'google'
  
  const webcalUrl = userId ? generateWebcalSubscriptionURL(userId) : '';
  const googleUrl = userId ? generateGoogleCalendarSubscriptionURL(userId) : '';

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      toast.success('URL gekopieerd naar klembord!');
    }).catch(() => {
      toast.error('Kon URL niet kopiëren');
    });
  };

  const handleOneTimeExport = (type) => {
    if (viewings.length === 0) {
      toast.error('Geen bezichtigingen om te exporteren');
      return;
    }
    
    if (type === 'apple') {
      viewings.forEach(viewing => onExportToApple(viewing));
      toast.success(`${viewings.length} bezichtigingen geëxporteerd naar Apple Calendar`);
    } else {
      viewings.forEach(viewing => onExportToGoogle(viewing));
      toast.success(`${viewings.length} bezichtigingen geopend in Google Calendar`);
    }
    onClose();
  };

  if (!syncType) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-gradient-to-r from-ios-blue to-ios-blue-dark text-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Synchroniseren met Agenda</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-gray-600">
              Kies hoe je je StudyStay agenda wilt synchroniseren:
            </p>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setSyncType('apple')}
                className="flex flex-col items-center justify-center space-y-3 p-6 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all hover:scale-105"
              >
                <svg className="w-12 h-12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <span className="font-semibold">Apple Calendar</span>
              </button>
              <button
                onClick={() => setSyncType('google')}
                className="flex flex-col items-center justify-center space-y-3 p-6 bg-white text-gray-700 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-all hover:scale-105"
              >
                <svg className="w-12 h-12" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="font-semibold">Google Calendar</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-ios-blue to-ios-blue-dark text-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">
              {syncType === 'apple' ? 'Apple Calendar Sync' : 'Google Calendar Sync'}
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSyncType(null)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-sm"
              >
                Terug
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {syncType === 'apple' ? (
            <>
              <div className="bg-blue-50 border-l-4 border-ios-blue p-4 rounded">
                <h3 className="font-semibold text-gray-900 mb-2">Automatische Synchronisatie</h3>
                <p className="text-gray-700 text-sm mb-4">
                  Voeg je StudyStay agenda toe als een geabonneerde kalender. Wijzigingen worden automatisch gesynchroniseerd!
                </p>
                
                <div className="space-y-3">
                  <div className="bg-white p-4 rounded border border-gray-200">
                    <label className="text-sm font-medium text-gray-600 block mb-2">
                      Webcal URL:
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        readOnly
                        value={webcalUrl}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-gray-50"
                      />
                      <button
                        onClick={() => handleCopyUrl(webcalUrl)}
                        className="px-4 py-2 bg-ios-blue text-white rounded hover:bg-ios-blue-dark transition-colors text-sm"
                      >
                        Kopieer
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Instructies:</h4>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                      <li>Open Apple Calendar op je Mac of iPhone</li>
                      <li>Ga naar "Bestand" → "Nieuwe agenda-abonnement" (Mac) of "Instellingen" → "Accounts" → "Account toevoegen" → "Ander" (iPhone)</li>
                      <li>Plak de bovenstaande URL</li>
                      <li>Klik op "Abonneren"</li>
                      <li>Je bezichtigingen verschijnen nu automatisch in Apple Calendar!</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h3 className="font-semibold text-gray-900 mb-2">Eenmalige Export</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Of download alle bezichtigingen als .ics bestand om handmatig te importeren:
                </p>
                <button
                  onClick={() => handleOneTimeExport('apple')}
                  className="w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Download Alle Bezichtigingen ({viewings.length})
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <h3 className="font-semibold text-gray-900 mb-2">Automatische Synchronisatie</h3>
                <p className="text-gray-700 text-sm mb-4">
                  Voeg je StudyStay agenda toe aan Google Calendar. Wijzigingen worden automatisch gesynchroniseerd!
                </p>
                
                <div className="space-y-3">
                  <div className="bg-white p-4 rounded border border-gray-200">
                    <label className="text-sm font-medium text-gray-600 block mb-2">
                      Calendar Feed URL:
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        readOnly
                        value={googleUrl}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-gray-50"
                      />
                      <button
                        onClick={() => handleCopyUrl(googleUrl)}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                      >
                        Kopieer
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Instructies:</h4>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                      <li>Ga naar <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Calendar</a></li>
                      <li>Klik op het "+" icoon naast "Andere agenda's"</li>
                      <li>Kies "Via URL toevoegen"</li>
                      <li>Plak de bovenstaande URL</li>
                      <li>Klik op "Agenda toevoegen"</li>
                      <li>Je bezichtigingen verschijnen nu automatisch in Google Calendar!</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h3 className="font-semibold text-gray-900 mb-2">Eenmalige Export</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Of open alle bezichtigingen in Google Calendar om handmatig toe te voegen:
                </p>
                <button
                  onClick={() => handleOneTimeExport('google')}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Open in Google Calendar ({viewings.length} bezichtigingen)
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

